import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    X, Download, Loader2, CalendarDays, FileSpreadsheet, FileText, FileCode, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchAnalyticsExportOptions } from '../api/analyticsApi';
import { startAnalyticsExport } from '../exportRunner';

/**
 * Analytics > Export.
 *
 * Christian, 11 Sep 2026: "if I want to export portal analytics I can click a
 * button and choose which dates I want to export ... choose to export portal
 * analytics, crm analytics or executive and what dates".
 *
 * Three choices, in the order he described them: WHAT (environment), WHICH
 * TABS, and WHEN (dates). The tab list is fetched rather than hard-coded. The
 * backend is the one that knows which tabs it can build and which environments
 * this user may have, so the dialog cannot offer a tab that would come back
 * empty or a scope that would come back 403.
 *
 * The file is built by the backend from the same endpoints that draw the tabs,
 * so an export always agrees with the screen.
 *
 * Pressing Export closes this dialog immediately and hands the work to
 * ../exportRunner, which lives outside the component tree. A wide export takes
 * a moment to build whatever we do, so the user is not made to sit and watch
 * it: they carry on working and the file arrives.
 */

const PRESETS = [
    { id: '7d', label: 'Last 7 days', days: 7 },
    { id: '30d', label: 'Last 30 days', days: 30 },
    { id: '90d', label: 'Last 90 days', days: 90 },
    { id: '365d', label: 'Last 12 months', days: 365 },
    { id: 'fy', label: 'This financial year' },
    { id: 'overall', label: 'Overall (all time)' },
];

// Same anchor the Report page uses for its "Overall (all time)" range: a fixed
// date safely before any platform data, so the export covers the full history.
const OVERALL_START = new Date('2020-01-01T00:00:00.000Z');

/**
 * Three jobs, three files.
 *
 * Excel is for working the numbers, CSV is for feeding another system, and
 * HTML is a written report: one clean, self-contained page with its own
 * styling and no CRM around it, so it can be emailed or printed as it is.
 */
const FORMATS = [
    {
        id: 'xlsx',
        label: 'Excel',
        icon: FileSpreadsheet,
        hint: 'A workbook with one sheet per tab. Best for sorting and totalling the numbers yourself.',
    },
    {
        id: 'html',
        label: 'HTML report',
        icon: FileCode,
        hint: 'A clean, presentable report in a single file. Opens in any browser, prints tidily, and can be sent on as it is.',
    },
    {
        id: 'csv',
        label: 'CSV',
        icon: FileText,
        hint: 'Plain text, for loading into another system.',
    },
];

const iso = (d) => d.toISOString().slice(0, 10);

const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d;
};

/** 1 July to today, the agency's year, the one the CRM tabs default to. */
const financialYearStart = () => {
    const now = new Date();
    const year = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
    return new Date(year, 6, 1);
};

const AnalyticsExportModal = ({ isOpen, onClose, environment = 'portal', dateRange }) => {
    const [scopes, setScopes] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [scope, setScope] = useState(environment);
    const [selected, setSelected] = useState([]);      // dataset keys; [] = everything
    const [preset, setPreset] = useState('30d');
    const [startDate, setStartDate] = useState(iso(daysAgo(30)));
    const [endDate, setEndDate] = useState(iso(new Date()));
    const [fileFormat, setFileFormat] = useState('xlsx');

    const applyPreset = useCallback((id) => {
        setPreset(id);
        if (id === 'custom') return;
        const found = PRESETS.find((p) => p.id === id);
        if (!found) return;
        let start;
        if (found.days) start = daysAgo(found.days);
        else if (id === 'overall') start = OVERALL_START;
        else start = financialYearStart();
        setStartDate(iso(start));
        setEndDate(iso(new Date()));
    }, []);

    // Open on the environment the user is looking at, and on the range they
    // already picked on the page, because the export they want is nearly always
    // the screen in front of them.
    useEffect(() => {
        if (!isOpen) return;
        setScope(environment);
        setSelected([]);

        const start = dateRange?.start ? new Date(dateRange.start) : null;
        const end = dateRange?.end ? new Date(dateRange.end) : null;
        if (start && end && !Number.isNaN(start.valueOf()) && !Number.isNaN(end.valueOf())) {
            // The page's "Overall (all time)" range maps to that preset; any other
            // explicit range opens as Custom with those dates.
            if (start <= OVERALL_START) {
                applyPreset('overall');
            } else {
                setPreset('custom');
                setStartDate(iso(start));
                setEndDate(iso(end));
            }
        } else {
            applyPreset('30d');
        }
    }, [isOpen, environment, dateRange, applyPreset]);

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setLoadingOptions(true);
        fetchAnalyticsExportOptions()
            .then((data) => {
                if (cancelled) return;
                setScopes(data?.scopes || []);
            })
            .catch(() => {
                if (!cancelled) toast.error('Could not load the export options');
            })
            .finally(() => {
                if (!cancelled) setLoadingOptions(false);
            });
        return () => { cancelled = true; };
    }, [isOpen]);

    const currentScope = useMemo(
        () => scopes.find((s) => s.id === scope) || scopes[0],
        [scopes, scope]
    );
    const datasets = currentScope?.datasets || [];
    const everything = selected.length === 0;

    const toggleDataset = (key) => {
        setSelected((prev) => {
            // First tick out of "Everything" means "only this one", which is
            // what clicking a single tab plainly looks like it should do.
            if (!prev.length) return [key];
            const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
            return next;
        });
    };

    const handleScopeChange = (id) => {
        if (id === scope) return;
        setScope(id);
        setSelected([]);   // dataset keys differ per environment
    };

    const handleExport = async () => {
        if (!startDate || !endDate) {
            toast.error('Pick a start and end date');
            return;
        }
        if (startDate > endDate) {
            toast.error('The start date is after the end date');
            return;
        }

        // Hand it to the runner and get out of the way. The request lives
        // outside this component, so closing the dialog does not cancel it and
        // the user can carry on working while it builds.
        startAnalyticsExport({
            scope,
            startDate,
            endDate,
            datasets: selected,
            fileFormat,
            label: currentScope?.label || scope,
        });
        onClose?.();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-teal-50 rounded-lg">
                            <Download className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">Export Analytics</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Choose what to export and the dates it should cover
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 overflow-y-auto space-y-6">
                    {loadingOptions ? (
                        <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading export options…
                        </div>
                    ) : (
                        <>
                            {/* What to export */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-2">
                                    What do you want to export?
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {scopes.map((s) => (
                                        <button
                                            key={s.id}
                                            type="button"
                                            onClick={() => handleScopeChange(s.id)}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                                                scope === s.id
                                                    ? 'bg-[#55C5D0] text-white border-[#55C5D0]'
                                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Dates */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-2">
                                    Which dates?
                                </label>
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {[...PRESETS, { id: 'custom', label: 'Custom' }].map((p) => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => applyPreset(p.id)}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                                                preset === p.id
                                                    ? 'bg-gray-900 text-white border-gray-900'
                                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <div className="flex-1">
                                        <span className="block text-[11px] font-medium text-gray-500 mb-1">From</span>
                                        <div className="relative">
                                            <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                            <input
                                                type="date"
                                                value={startDate}
                                                max={endDate}
                                                onChange={(e) => {
                                                    setStartDate(e.target.value);
                                                    setPreset('custom');
                                                }}
                                                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <span className="block text-[11px] font-medium text-gray-500 mb-1">To</span>
                                        <div className="relative">
                                            <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                            <input
                                                type="date"
                                                value={endDate}
                                                min={startDate}
                                                onChange={(e) => {
                                                    setEndDate(e.target.value);
                                                    setPreset('custom');
                                                }}
                                                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Which tabs */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-xs font-semibold text-gray-600">
                                        Which tabs? {everything
                                            ? <span className="font-normal text-gray-400">Everything</span>
                                            : <span className="font-normal text-gray-400">{selected.length} selected</span>}
                                    </label>
                                    {!everything && (
                                        <button
                                            type="button"
                                            onClick={() => setSelected([])}
                                            className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                                        >
                                            Select everything
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {datasets.map((d) => {
                                        const on = everything || selected.includes(d.key);
                                        return (
                                            <button
                                                key={d.key}
                                                type="button"
                                                onClick={() => toggleDataset(d.key)}
                                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left text-xs font-medium transition-colors ${
                                                    on
                                                        ? 'bg-teal-50 border-teal-200 text-teal-800'
                                                        : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                                                }`}
                                            >
                                                <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${
                                                    on ? 'bg-teal-600 border-teal-600' : 'border-gray-300'
                                                }`}>
                                                    {on && <Check className="w-3 h-3 text-white" />}
                                                </span>
                                                <span className="truncate">{d.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-[11px] text-gray-400 mt-2">
                                    Every tab is included unless you pick some. A very wide export can
                                    take a moment, so pick fewer tabs or a shorter range if it times out.
                                </p>
                            </div>

                            {/* Format */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-2">
                                    File type
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {FORMATS.map((f) => {
                                        const Icon = f.icon;
                                        const on = fileFormat === f.id;
                                        return (
                                            <button
                                                key={f.id}
                                                type="button"
                                                onClick={() => setFileFormat(f.id)}
                                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                                                    on
                                                        ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                                                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                                }`}
                                            >
                                                <Icon className="w-4 h-4" />
                                                {f.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-[11px] text-gray-400 mt-2">
                                    {FORMATS.find((f) => f.id === fileFormat)?.hint}
                                </p>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleExport}
                        disabled={loadingOptions || !datasets.length}
                        className="flex items-center gap-2 px-5 py-2 bg-[#55C5D0] text-white text-sm font-semibold rounded-lg hover:bg-[#45b5c0] transition-colors disabled:opacity-50"
                    >
                        <Download className="w-4 h-4" />
                        Export
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsExportModal;
